import { motion } from "framer-motion";

/**
 * Anabiya Decor — bespoke crest logo.
 * A hand-drawn AD ligature inside a laurel-crown wordmark. All colours pulled from the
 * brand tokens (forest / gold / bone). Two variants:
 *   - <Logo variant="crest" />   — square crest for admin, favicons, seals
 *   - <Logo variant="wordmark" /> — inline horizontal lock-up for the navbar
 */
export const Logo = ({ variant = "wordmark", className = "", withTagline = false }) => {
  if (variant === "wordmark") {
    return (
      <div className={`inline-flex items-center leading-none ${className}`}>
        <img
          src="/logo.png"
          alt="Anabiya Decor — Luxury Bedding & Home Textiles"
          width="180"
          height="60"
          className="h-11 md:h-12 lg:h-14 w-auto object-contain"
          fetchPriority="high"
        />
      </div>
    );
  }
  if (variant === "crest") {
    return (
      <motion.svg
        initial={{ opacity: 0, scale: 0.92, rotate: -6 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.1, ease: [0.7, 0, 0.2, 1] }}
        viewBox="0 0 120 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="Anabiya Decor crest"
        className={className}
      >
        {/* Outer bone medallion */}
        <circle cx="60" cy="60" r="58" fill="hsl(36 30% 96%)" stroke="hsl(150 60% 21%)" strokeWidth="0.75" />
        {/* Concentric gold ring */}
        <circle cx="60" cy="60" r="52" fill="none" stroke="hsl(38 55% 55%)" strokeWidth="0.4" strokeDasharray="1 3" />
        {/* Laurel leaves */}
        <path
          d="M15 60 Q 30 30 60 25 Q 90 30 105 60"
          fill="none"
          stroke="hsl(150 60% 21%)"
          strokeWidth="0.6"
        />
        <g fill="hsl(150 60% 21%)">
          {[22, 30, 38, 46, 74, 82, 90, 98].map((cx, i) => (
            <ellipse key={i} cx={cx} cy={i < 4 ? 44 + i * 2 : 44 + (7 - i) * 2} rx="3" ry="1.4" transform={`rotate(${i < 4 ? -32 + i * 6 : 32 - (7 - i) * 6} ${cx} ${i < 4 ? 44 + i * 2 : 44 + (7 - i) * 2})`} />
          ))}
        </g>
        {/* AD ligature */}
        <g fontFamily="'Fraunces', 'DM Serif Display', serif" textAnchor="middle" fill="hsl(150 60% 21%)">
          <text x="45" y="76" fontSize="46" fontStyle="italic" fontWeight="500">A</text>
          <text x="78" y="76" fontSize="46" fontStyle="italic" fontWeight="500" fill="hsl(38 55% 55%)">D</text>
        </g>
        {/* Bottom marque */}
        <text
          x="60"
          y="102"
          textAnchor="middle"
          fontFamily="'Inter', system-ui, sans-serif"
          fontSize="6"
          letterSpacing="2.5"
          fill="hsl(150 60% 21%)"
        >
          MMXVI · MUMBAI
        </text>
      </motion.svg>
    );
  }

  // Wordmark — horizontal lock-up with crest to the left of the word.
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <svg viewBox="0 0 44 44" width="30" height="30" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        {/* Diamond frame */}
        <g transform="rotate(45 22 22)">
          <rect x="6" y="6" width="32" height="32" fill="none" stroke="hsl(150 60% 21%)" strokeWidth="0.9" />
          <rect x="10" y="10" width="24" height="24" fill="none" stroke="hsl(38 55% 55%)" strokeWidth="0.5" />
        </g>
        {/* AD monogram */}
        <text
          x="22"
          y="27"
          textAnchor="middle"
          fontFamily="'Fraunces', 'DM Serif Display', serif"
          fontSize="18"
          fontStyle="italic"
          fontWeight="500"
          fill="hsl(150 60% 21%)"
        >
          A
        </text>
        <text
          x="30"
          y="30"
          textAnchor="middle"
          fontFamily="'Fraunces', 'DM Serif Display', serif"
          fontSize="12"
          fontStyle="italic"
          fill="hsl(38 55% 55%)"
        >
          D
        </text>
      </svg>
      <div className="flex flex-col leading-none">
        <div className="flex items-baseline gap-1.5">
          <span className="font-display text-forest text-[22px] md:text-[26px] leading-none tracking-[-0.01em]">
            Anabiya
          </span>
          <span className="font-display italic text-gold text-[17px] md:text-[19px] leading-none">
            Decor
          </span>
        </div>
        {withTagline && (
          <span className="mt-1 overline text-forest/50 text-[9px] tracking-[0.25em]">
            HOUSE OF TEXTILE · MMXVI
          </span>
        )}
      </div>
    </div>
  );
};
